<template>
<!-- eslint-disable max-len -->
<span>
  <top-bar v-if="narrative.showNavbar" :show-player="narrative.type === 'leftDecisionBubble'" :show-right-player="narrative.type === 'rightDecisionBubble'">
    <template :slot="narrative.showMetrics ? 'challenge' : 'default'">
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks
        This is what the following line used to be:
        {{ narrativeNavbarText }}
      -->
      <p v-html="narrativeNavbarText"></p>
    </template>
    <cash-navbar v-if="narrative.showMetrics" slot="cash"></cash-navbar>
    <metrics-navbar v-if="narrative.showMetrics" slot="metrics"></metrics-navbar>
  </top-bar>

  <transition
    :enter-active-class="narrative.type === 'rightDecisionBubble' && rightCharacterAnimateEnter ? 'animated slideInRight' : narrative.type === 'leftDecisionBubble' && leftCharacterAnimateEnter ? 'animated slideInLeft' : ''"
    :leave-active-class="narrative.type === 'rightDecisionBubble' && rightCharacterAnimateLeave ? 'animated slideOutRight' : narrative.type === 'leftDecisionBubble' && leftCharacterAnimateLeave ? 'animated slideOutLeft' : ''"
  >
    <player
      v-if="
        narrative.type === 'rightDecisionBubble' && showRightCharacter || narrative.type === 'leftDecisionBubble' && showLeftCharacter
      "
      :angled="
        (narrative.type === 'leftDecisionBubble' && !!(narrative.rightCharacter))
        || (narrative.type === 'rightDecisionBubble' && !!(narrative.leftCharacter))
      "
      :right="narrative.type === 'rightDecisionBubble'"
      :full="!narrative.showNavbar"
      :variant-override="
        (decisionMade && narrative.type === 'rightDecisionBubble')
          ? narrative.rightExtraVariant
          : (decisionMade ? narrative.leftExtraVariant : '')"
      :tooltip-target="(narrative.type === 'rightDecisionBubble') ? 'characterRight' : 'characterLeft'"
    ></player>
  </transition>

  <transition
    :enter-active-class="rightCharacterAnimateEnter ? 'animated slideInRight' : ''"
    :leave-active-class="rightCharacterAnimateLeave ? 'animated slideOutRight' : ''"
  >
    <player
      v-if="showRightCharacter && narrative.type === 'leftDecisionBubble' && narrative.rightPlayer"
      :angled="narrative.type === 'rightDecisionBubble' && !!(narrative.leftCharacter)"
      right
      :full="!narrative.showNavbar"
      :variant-override="decisionMade ? narrative.rightExtraVariant : ''"
      key="player"
      tooltip-target="characterRight"
    ></player>
    <narrator
      v-else-if="
        showRightCharacter && narrative.type === 'leftDecisionBubble'
        && (narrative.rightCharacter || narrative.rightNarrator)
      "
      :full="!narrative.showNavbar"
      :character="characters[narrative.rightCharacter]"
      :variant-override="decisionMade ? narrative.rightExtraVariant : ''"
      key="character"
      tooltip-target="characterRight"
    ></narrator>
  </transition>

  <transition
    :enter-active-class="leftCharacterAnimateEnter ? 'animated slideInLeft' : ''"
    :leave-active-class="leftCharacterAnimateLeave ? 'animated slideOutLeft' : ''"
  >
    <player
      v-if="showLeftCharacter && narrative.type === 'rightDecisionBubble' && narrative.leftPlayer"
      :angled="narrative.type === 'leftDecisionBubble' && !!(narrative.rightCharacter)"
      :full="!narrative.showNavbar"
      :variant-override="decisionMade ? narrative.leftExtraVariant : ''"
      key="player"
      tooltip-target="characterLeft"
    ></player>
    <narrator
      v-else-if="
        showLeftCharacter && narrative.type === 'rightDecisionBubble'
        && (narrative.leftCharacter || narrative.leftNarrator)
      "
      left
      :full="!narrative.showNavbar"
      :character="characters[narrative.leftCharacter]"
      :variant-override="decisionMade ? narrative.leftExtraVariant : ''"
      key="character"
      tooltip-target="characterLeft"
    ></narrator>
  </transition>

  <bottom-bar
    v-if="(native)"
    :choiceHeight="(!decisionMade) ? choicesHeight : '0'"
    :back="(decisionMade || enableBackToIntro) ? onClickBack : null"
    :next="(decisionMade || (decision && decision.type === 'multiselect')) ? onClickApprove : null"
  >
    <template slot="choices" v-if="native">
      <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
        <choice-bubble
          v-if="!decisionMade && showChoice"
          :right="narrative.type === 'rightDecisionBubble'"
          :style="choiceBubbleStyle"
          :decision="decision"
          @input="onChoice"
          key="choice-bubble"
          id="choices"
          ref="choiceBubble"
          :shuffled-choice-order="shuffledIndices"
        >
        </choice-bubble>
      </transition>
    </template>
    <template v-if="decisionMade || (showChoice && decision && decision.type === 'multiselect')" slot="button">
      <span
        v-html="
          decisionMade
            ? ((decision && decision.submitButtonText) ? decision.submitButtonText : 'Reply')
            : 'Select'
        "
      ></span>
    </template>
  </bottom-bar>
  <bottom-bar
    v-else-if="enableBackToIntro && !decisionMade"
    :back="onClickBack"
    decision-style
  >
    <span slot="back">
      <span v-html="(game && game.backButtonDefaultText) ? game.backButtonDefaultText : 'Back'"></span>
    </span>
  </bottom-bar>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <narrator-bubble
      v-if="showNarratorBubble"
      class="narrator-bubble"
      :style="narratorBubbleStyle"
      :left="narrative.speaker === 'left'"
      :center="narrative.speaker === 'center'"
      :thought="narrative.thoughtBubble"
      :width="narratorBubbleWidth"
    >
      <div style="display: flex; flex-direction: column;" :style="narratorBubbleContentStyle">
        <!--
          Never use v-html with end-user generated content, because it's a vector for XSS attacks
        -->
        <p style="max-height: 300px; overflow-y: auto;" v-html="narrativeText"></p>
      </div>
    </narrator-bubble>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <choice-bubble
      v-if="starfighter && !decisionMade && showChoice && decision"
      :right="narrative.type === 'rightDecisionBubble'"
      :style="choiceBubbleStyle"
      :decision="decision"
      @input="onChoice"
      key="choiceBubble"
      id="choices"
      ref="choiceBubble"
      :shuffled-choice-order="shuffledIndices"
      :submit-button-text="decision.choiceSelectButtonText"
      :dynamic-height-choices="decision.dynamicHeightChoices"
    ></choice-bubble>
    <reason-bubble
      v-else-if="decisionMade && decision"
      class="reason-bubble"
      :style="reasonBubbleStyle"
      :right="narrative.type === 'rightDecisionBubble'"
      :decision="decision"
      :choice="choice"
      :disable-reasoning-form="decision.disableReasoningForm"
      :prevent-reasoning-input-override="decision.preventReasoningInputOverride"
      :decision-reasoning-minimum-length="decision.reasoningMinimumLength"
      :decision-reasoning-minimum-length-override="decision.reasoningMinimumLengthOverride"
      :submit-button-text="decision.submitButtonText"
      :back-button-text="decision.backButtonText"
      v-model="reason"
      @back="onReasonBack"
      @input="onReasonSubmit"
      key="reasonBubble"
      ref="reasonBubble"
    ></reason-bubble>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import Paragraphs from '../../ui/display/Paragraphs';
import TopBar from '../ui/TopBar';
import MetricsNavbar from '../ui/MetricsNavbar';
import CashNavbar from '../ui/CashNavbar';
import Player from '../ui/Player';
import Narrator from '../ui/Narrator';
import NarratorBubble from '../ui/NarratorBubble';
import BottomBar from '../ui/BottomBar';
import ChoiceBubble from '../ui/ChoiceBubble';
import ReasonBubble from '../ui/ReasonBubble';

export default {
  // $emit 'input' on complete
  props: {
    decision: { type: Object }
  },
  components: {
    Paragraphs,
    TopBar,
    MetricsNavbar,
    CashNavbar,
    Player,
    Narrator,
    NarratorBubble,
    BottomBar,
    ChoiceBubble,
    ReasonBubble
  },
  data() {
    return {
      decisionMade: false,
      choice: undefined,
      selectedChoiceIndex: undefined,
      reason: '',
      savedReason: '',
      showNarratorBubble: false,
      showChoice: false,
      showLeftCharacter: false,
      showRightCharacter: false,
      choicesElement: null,
      /** used for analytics */
      timeOnDecisionReferenceDate: new Date(),
    };
  },
  computed: {
    ...mapGetters([
      'characters',
      'tooltip',
      'narrative',
      'individualDecision',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'leftCharacterAnimateEnter',
      'leftCharacterAnimateLeave',
      'rightCharacterAnimateEnter',
      'rightCharacterAnimateLeave',
      'game',
      'activeClass',
      'starfighter',
      'native'
    ]),
    enableBackToIntro() {
      return (this.decision && this.decision.forceEnableBack)
        || (this.game.enableBack && !(this.decision && this.decision.preventEnableBack));
    },
    choiceDisplayType() {
      if (!(this.decision) || this.decision.type !== 'choice') {
        return 'list';
      }
      if (!(this.decision.preventChoiceDisplayTypeOverride)
        && this.activeClass && this.activeClass.choiceDisplayTypeOverride
        && this.activeClass.choiceDisplayTypeOverride !== 'Do Not Force') {
        switch (this.activeClass.choiceDisplayTypeOverride) {
          case 'List': return 'list';
          case 'Carousel': return 'carousel';
          default: return 'list';
        }
      }
      if (!(this.decision.choiceDisplayType)) return 'list';
      return this.decision.choiceDisplayType;
    },
    choiceDisplayTypeWithoutOverride() {
      if (!(this.decision.choiceDisplayType)) return 'list';
      return this.decision.choiceDisplayType;
    },
    choiceDisplayTypeOverriddenToCarousel() {
      return this.choiceDisplayType === 'carousel'
        && this.choiceDisplayType !== this.choiceDisplayTypeWithoutOverride;
    },
    choiceBubbleStyle() {
      let leftVal = ((this.native) ? '350px' : '360px');
      let bottomVal = '70px';
      if (!(this.decision.dynamicHeightChoices) && this.choiceDisplayType === 'list' && this.decision.wrongChoicesMade) {
        const count =
          Math.max(
            0,
            this.playerDecision.wrongChoicesMade
              - Math.max(
                0,
                this.playerDecision.wrongChoicesMade + this.playerDecision.choices.length - 5
              )
          );

        const extraBottom = (this.decision.displayOneLineChoices)
          ? (count * 80)
          : (Math.floor(count / 2) * 160);

        bottomVal = `${70 + extraBottom}px`;
      }
      if (this.narrative.choiceXOffset) {
        leftVal = `calc(${leftVal} + ${this.narrative.choiceXOffset})`;
      }

      if (this.decision.choices.length >= 5 && !(this.choiceDisplayTypeOverriddenToCarousel)) {
        bottomVal = `calc(${bottomVal} + ${this.narrative.choiceYOffset} - 180px)`;
      } else if (this.decision.choices.length >= 4 && !(this.choiceDisplayTypeOverriddenToCarousel)) {
        bottomVal = `calc(${bottomVal} + ${this.narrative.choiceYOffset} - 80px)`;
      } else if (this.narrative.choiceYOffset) {
        bottomVal = this.choiceDisplayTypeOverriddenToCarousel
          ? `max(40px, calc(${bottomVal} + ${this.narrative.choiceYOffset} - 100px))`
          : `calc(${bottomVal} + ${this.narrative.choiceYOffset})`;
      }

      return {
        left: leftVal,
        bottom: bottomVal
      };
    },
    choicesHeight() {
      return `${this.choicesElement ? this.choicesElement.offsetHeight + 30 : 0}px`;
    },
    narratorBubbleExtendsOffScreen() {
      return this.native && this.narrative.rightCharacter;
    },
    narratorBubbleWidth() {
      if (this.starfighter) {
        return '900px';
      }
      if (this.narratorBubbleExtendsOffScreen) {
        return '1400px';
      }
      return '1050px';
    },
    narratorBubbleStyle() {
      // hardcoded defaults based on theme
      let topVal =
        ((this.native) ? '65px' : ((this.narrative.showNavbar) ? '150px' : '20px'));
      let leftVal = ((this.native) ? '390px' : '360px');
      let totalYOffset = '';
      let totalXOffset = '';
      // offset from narrative offset setting
      if (this.narrative.bubbleYOffset) {
        totalYOffset += ` - ${this.narrative.bubbleYOffset}`;
      }
      if (this.narrative.bubbleXOffset) {
        totalXOffset += ` + ${this.narrative.bubbleXOffset}`;
      }
      // output final position style
      if (totalYOffset) {
        topVal = `calc(${topVal}${totalYOffset})`;
      }
      if (totalXOffset) {
        leftVal = `calc(${leftVal}${totalXOffset})`;
      }
      return {
        top: topVal,
        left: leftVal
      };
    },
    narratorBubbleContentStyle() {
      if (this.native) {
        return {
          margin: (this.narratorBubbleExtendsOffScreen ? '-54px 454px -42px -16px' : '-54px -24px -42px -16px'),
          'max-height': '324px',
          'line-height': '1.5'
        };
      }
      return '';
    },
    reasonBubbleStyle() {
      // hardcoded defaults based on theme
      let topVal = ((this.narrative.showNavbar) ? '150px' : '50px');
      let leftVal = '360px';
      let totalYOffset = '';
      let totalXOffset = '';
      // offset from narrative offset setting
      if (this.narrative.reasonYOffset) {
        totalYOffset += ` - ${this.narrative.reasonYOffset}`;
      }
      if (this.narrative.reasonXOffset) {
        totalXOffset += ` + ${this.narrative.reasonXOffset}`;
      }
      // output final position style
      if (totalYOffset) {
        topVal = `calc(${topVal}${totalYOffset})`;
      }
      if (totalXOffset) {
        leftVal = `calc(${leftVal}${totalXOffset})`;
      }
      return {
        top: topVal,
        left: leftVal
      };
    },
    shuffledIndices() {
      if (this.decision && this.decision.choices) {
        return this.shuffle(this.decision.choices.map((_, index) => index));
      }
      return null;
    },
  },
  methods: {
    ...mapActions([
      'playVoice',
      'stopSounds',
      'submitIndividualDecision',
      'rewindToTooltipTarget',
      'addNarratives'
    ]),
    shuffle(array) {
      // ***
      // Fisher-Yates JS implementation from: https://stackoverflow.com/a/2450976
      // ***
      let currentIndex = array.length;
      let temporaryValue;
      let randomIndex;
      // While there remain elements to shuffle...
      while (currentIndex !== 0) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }
      return array;
    },
    async show() {
      this.showLeftCharacter = true;
      this.showRightCharacter = true;
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.showNarratorBubble = true;
      if (this.narrative.textVoiceLine) this.playVoice({ line: this.narrative.textVoiceLine });
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.showChoice = true;
      await Vue.nextTick();
      this.choicesElement = document.getElementById('choices');
    },
    async reset() {
      this.decisionMade = false;
      this.choice = undefined;
      this.selectedChoiceIndex = undefined;
      this.reason = '';
      this.savedReason = '';
      this.showLeftCharacter = !this.leftCharacterAnimateEnter;
      this.showRightCharacter = !this.rightCharacterAnimateEnter;
      this.showNarratorBubble = false;
      this.showChoice = false;
      if (this.leftCharacterAnimateEnter || this.rightCharacterAnimateEnter) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.showLeftCharacters = true;
        this.showRightCharacters = true;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.showNarratorBubble = true;
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.showChoice = true;
    },
    async hide() {
      this.decisionMade = false;
      this.choice = undefined;
      this.selectedChoiceIndex = undefined;
      this.reason = '';
      this.savedReason = '';
      this.showLeftCharacter = !this.leftCharacterAnimateLeave;
      this.showRightCharacter = !this.rightCharacterAnimateLeave;
    },
    onChoice({ choice, selectedChoiceIndex }) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      this.choice = choice;
      if (selectedChoiceIndex === this.selectedChoiceIndex) {
        this.reason = this.savedReason;
      } else {
        this.reason = '';
        this.savedReason = '';
      }
      this.selectedChoiceIndex = selectedChoiceIndex;
      this.decisionMade = true;
      this.showNarratorBubble = false;
      this.showChoice = false;
    },
    async onReasonBack(reason) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      await Vue.nextTick();
      this.stopSounds(); // stops any choice description voice line (or any sfx played by Reason if that is added)
      this.showNarratorBubble = true;
      this.showChoice = true;
      this.savedReason = reason;
      this.decisionMade = false;
      await new Promise(resolve => setTimeout(resolve, 475)); // wait for tooltip exit delay
      await this.rewindToTooltipTarget({ target: 'reasonAndConfirmChoiceBubble', tooltips: this.decision.tooltips || [] });
    },
    async onReasonSubmit(reason) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      const { choice } = this;
      const secondsSpentOnDecision = (Date.now() - this.timeOnDecisionReferenceDate.valueOf()) / 1000;

      if (choice.narratives && choice.narratives.length > 0) {
        this.addNarratives({ narratives: choice.narratives });
      }

      if (choice.wrong) {
        const { narratives } = this.individualDecision;
        this.addNarratives({ narratives });
      }

      for (let i = 3; i > 0; --i) {
        try {
          await this.submitIndividualDecision({
            choice,
            reason,
            duration: secondsSpentOnDecision,
          });
          await this.hide();
          return this.$emit('input');
        } catch (err) {
          if (this.$router.handleGameError(err)) return;
        }
      }
      this.$router.showGameErrorCritical();
    },
    getReasonRef() {
      if (this.$refs && this.$refs.reasonBubble) {
        return this.$refs.reasonBubble;
      }
      return null;
    },
    async getReasonRefAsync() {
      while (this.getReasonRef() == null) {
        await new Promise(resolve => setTimeout(resolve, 250));
      }
      return this.getReasonRef();
    },
    getChoiceRef() {
      if (this.$refs && this.$refs.choiceBubble) {
        return this.$refs.choiceBubble;
      }
      return null;
    },
    async getChoiceRefAsync() {
      while (this.getChoiceRef() == null) {
        await new Promise(resolve => setTimeout(resolve, 250));
      }
      return this.getChoiceRef();
    },
    async onClickBack() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      if (this.decisionMade) {
        await ((await this.getReasonRefAsync()).onClickBack());
      } else {
        this.$emit('input', true); // narrative back
      }
    },
    async onClickApprove() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      if (this.decisionMade) {
        await ((await this.getReasonRefAsync()).onClickApprove());
      } else if (this.native && this.decision.type === 'multiselect') {
        await ((await this.getChoiceRefAsync()).onClickChoice());
      }
    }
  },
  watch: {
    async narrative(newValue, oldValue) {
      if (newValue && (!oldValue || newValue._id !== oldValue._id)) await this.reset();
    }
  },
  created() {
    this.showLeftCharacter = !this.leftCharacterAnimateEnter;
    this.showRightCharacter = !this.rightCharacterAnimateEnter;
  },
  async mounted() {
    this.timeOnDecisionReferenceDate = new Date();
    await this.show();
  }
};
</script>

<style lang="scss" scoped>
.narrator-bubble {
  max-height: 330px;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeDecisionBubble.vue